'use client';

import { Check, ChevronDown, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCart } from '@/components/cart/cart-provider';
import {
	getMarketDisplayFlag,
	getMarketKey,
	REST_OF_WORLD_MARKET,
	type StorefrontMarket,
	setBrowserMarketCookie,
	toCanonicalPath,
} from '@/lib/markets';

interface MarketSelectorProps {
	market: StorefrontMarket;
	supportedMarkets: StorefrontMarket[];
}

const PRIORITY_MARKET_COUNTRIES = ['us', 'ca', 'gb', 'au'];

interface MarketOptionsListProps {
	options: StorefrontMarket[];
	selectedMarketKey: string;
	labelledBy: string;
	onSelect: (market: StorefrontMarket) => void;
}

function MarketOptionsList({
	options,
	selectedMarketKey,
	labelledBy,
	onSelect,
}: MarketOptionsListProps) {
	return (
		<div role="listbox" aria-labelledby={labelledBy}>
			{options.map((option) => {
				const optionKey = getMarketKey(option);
				const isSelected = optionKey === selectedMarketKey;

				return (
					<button
						key={optionKey}
						type="button"
						role="option"
						aria-selected={isSelected}
						className={`flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm transition-colors hover:bg-neutral-100 ${
							isSelected
								? 'bg-neutral-950 font-semibold text-white hover:bg-neutral-950'
								: 'font-medium'
						}`}
						onClick={() => onSelect(option)}
					>
						<span className="text-lg leading-none" aria-hidden="true">
							{getMarketDisplayFlag(option)}
						</span>
						<span className="min-w-0 flex-1 truncate">{option.label}</span>
						{isSelected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
					</button>
				);
			})}
		</div>
	);
}

function getSortedMarketOptions(markets: StorefrontMarket[]) {
	const uniqueMarkets = new Map<string, StorefrontMarket>();

	for (const market of markets) {
		uniqueMarkets.set(getMarketKey(market), market);
	}

	return Array.from(uniqueMarkets.values()).sort((left, right) => {
		if (left.country === REST_OF_WORLD_MARKET.country) return 1;
		if (right.country === REST_OF_WORLD_MARKET.country) return -1;

		const leftPriority = PRIORITY_MARKET_COUNTRIES.indexOf(left.country);
		const rightPriority = PRIORITY_MARKET_COUNTRIES.indexOf(right.country);
		if (leftPriority !== -1 || rightPriority !== -1) {
			return (
				(leftPriority === -1 ? 999 : leftPriority) - (rightPriority === -1 ? 999 : rightPriority)
			);
		}

		return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
	});
}

export function MarketSelector({ market, supportedMarkets }: MarketSelectorProps) {
	const { updateBuyerIdentity } = useCart();
	const [isOpen, setIsOpen] = useState(false);
	const [isChanging, setIsChanging] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const mobileDialogRef = useRef<HTMLDivElement | null>(null);
	const selectedMarketKey = getMarketKey(market);
	const labelId = 'store-selector-label';
	const dialogTitleId = 'store-selector-dialog-title';
	const options = getSortedMarketOptions(
		supportedMarkets.some((option) => getMarketKey(option) === selectedMarketKey)
			? supportedMarkets
			: [market, ...supportedMarkets],
	);
	const selectedFlag = getMarketDisplayFlag(market);
	const selectedMarketCode =
		market.country === REST_OF_WORLD_MARKET.country ? 'ROW' : market.countryCode;

	useEffect(() => {
		if (!isOpen) return;

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (!rootRef.current?.contains(target) && !mobileDialogRef.current?.contains(target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [isOpen]);

	const handleMarketChange = (nextMarket: StorefrontMarket) => {
		if (getMarketKey(nextMarket) === selectedMarketKey || isChanging) return;

		setIsChanging(true);
		setIsOpen(false);
		setBrowserMarketCookie(nextMarket);
		void updateBuyerIdentity({ countryCode: nextMarket.countryCode });

		const nextPathname = toCanonicalPath(window.location.pathname);
		window.location.assign(`${nextPathname}${window.location.search}${window.location.hash}`);
	};

	return (
		<div ref={rootRef} className="relative inline-block max-w-full">
			<span id={labelId} className="sr-only">
				Country/Region
			</span>
			<button
				type="button"
				disabled={isChanging}
				className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-900/70 px-2.5 py-1.5 text-xs font-semibold leading-none text-neutral-300 transition-colors hover:border-neutral-700 hover:bg-neutral-900 hover:text-white disabled:opacity-60"
				aria-label={`Country/Region: ${selectedMarketCode}`}
				aria-expanded={isOpen}
				aria-haspopup="dialog"
				onClick={() => setIsOpen((current) => !current)}
			>
				<span className="text-base leading-none" aria-hidden="true">
					{selectedFlag}
				</span>
				<span>{selectedMarketCode}</span>
				<ChevronDown
					className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
					aria-hidden="true"
				/>
			</button>

			{isOpen && (
				<>
					<button
						type="button"
						className="fixed inset-0 z-[70] bg-black/55 sm:hidden"
						aria-label="Close country selector"
						onClick={() => setIsOpen(false)}
					/>

					<div className="absolute bottom-full right-0 z-50 mb-3 hidden max-h-[14rem] w-60 overflow-y-auto overscroll-contain rounded-sm border border-neutral-200 bg-white p-2 text-neutral-900 shadow-xl sm:block">
						<MarketOptionsList
							options={options}
							selectedMarketKey={selectedMarketKey}
							labelledBy={labelId}
							onSelect={handleMarketChange}
						/>
					</div>

					<div
						ref={mobileDialogRef}
						role="dialog"
						aria-modal="true"
						aria-labelledby={dialogTitleId}
						className="fixed inset-x-4 bottom-6 z-[80] overflow-hidden rounded-xl border border-neutral-200 bg-white text-neutral-900 shadow-2xl sm:hidden"
					>
						<div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
							<div>
								<p id={dialogTitleId} className="text-sm font-semibold text-neutral-950">
									Country/Region
								</p>
								<p className="mt-0.5 text-xs text-neutral-500">
									Prices and checkout update by region.
								</p>
							</div>
							<button
								type="button"
								className="rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-950"
								aria-label="Close country selector"
								onClick={() => setIsOpen(false)}
							>
								<X className="h-4 w-4" aria-hidden="true" />
							</button>
						</div>
						<div className="max-h-[min(70dvh,28rem)] overflow-y-auto overscroll-contain p-2">
							<MarketOptionsList
								options={options}
								selectedMarketKey={selectedMarketKey}
								labelledBy={dialogTitleId}
								onSelect={handleMarketChange}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
